// src/pages/instances/modals/AddEditInstanceModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, message, Switch } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { useTranslation } from "react-i18next";
import { Instance } from "types/superAdmin/instanceTypes";
import { isValidPhoneNumber } from "utils/validators";
import {
  CreateInstancePayloadForApi,
  UpdateInstancePayloadForApi,
} from "../../../services/superAdmin/instenceService";

type InstanceFormValues = {
  // instance
  name: string;
  establishmentName?: string;
  phoneNumber?: string;
  address?: string;
  zipCode?: string;
  academy?: string;
  prefecture?: string;
  commune?: string;
  directorName?: string;
  modelConfig?: {
    hrm?: boolean;
    finance?: boolean;
  };

  // director (create only)
  directorFirstName?: string;
  directorLastName?: string;
  directorEmail?: string;
  directorPhone?: string;
};

interface AddEditInstanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  instanceData?: Partial<Instance> | null; // when present -> edit mode
  onSave: (
    payload: CreateInstancePayloadForApi | UpdateInstancePayloadForApi
  ) => Promise<boolean | void>;
}

const AddEditInstanceModal: React.FC<AddEditInstanceModalProps> = ({
  isOpen,
  onClose,
  instanceData,
  onSave,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<InstanceFormValues>();
  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => Boolean(instanceData?.id), [instanceData?.id]);

  useEffect(() => {
    if (!isOpen) return;

    form.resetFields();

    form.setFieldsValue({
      name: instanceData?.name ?? "",
      establishmentName: instanceData?.establishmentName ?? "",
      phoneNumber: instanceData?.phoneNumber ?? "",
      address: instanceData?.address ?? "",
      zipCode: instanceData?.zipCode ?? "",
      academy: instanceData?.academy ?? "",
      prefecture: instanceData?.prefecture ?? "",
      commune: instanceData?.commune ?? "",
      directorName: instanceData?.directorName ?? "",
      modelConfig: {
        hrm: instanceData?.modelConfig?.hrm ?? false,
        finance: instanceData?.modelConfig?.finance ?? false,
      },

      ...(isEdit
        ? {}
        : {
            directorFirstName: "",
            directorLastName: "",
            directorEmail: "",
            directorPhone: "",
          }),
    });
  }, [isOpen, isEdit, instanceData, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const v = await form.validateFields();

      const modelConfig = {
        hrm: v.modelConfig?.hrm ?? false,
        finance: v.modelConfig?.finance ?? false,
      };

      if (isEdit) {
        // UPDATE: only instanceData
        const payload: UpdateInstancePayloadForApi = {
          instanceData: {
            name: v.name,
            establishmentName: v.establishmentName || undefined,
            phoneNumber: v.phoneNumber || undefined,
            address: v.address || undefined,
            zipCode: v.zipCode || undefined,
            academy: v.academy || undefined,
            prefecture: v.prefecture || undefined,
            commune: v.commune || undefined,
            directorName: v.directorName || undefined,
            modelConfig,
          },
        };

        const ok = await onSave(payload);
        if (ok !== false) {
          onClose();
          form.resetFields();
        }
      } else {
        // CREATE: instanceData + directorData
        const payload: CreateInstancePayloadForApi = {
          instanceData: {
            name: v.name,
            establishmentName: v.establishmentName || undefined,
            phoneNumber: v.phoneNumber || undefined,
            address: v.address || undefined,
            zipCode: v.zipCode || undefined,
            academy: v.academy || undefined,
            prefecture: v.prefecture || undefined,
            commune: v.commune || undefined,
            directorName: v.directorName || undefined,
            modelConfig,
          },
          directorData: {
            firstName: v.directorFirstName!,
            lastName: v.directorLastName!,
            email: v.directorEmail!,
            phoneNumber: v.directorPhone || undefined,
          },
        };

        const ok = await onSave(payload);
        if (ok !== false) {
          onClose();
          form.resetFields();
        }
      }
    } catch {
      message.error(t("validation.please_fill_all_required_fields"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      title={isEdit ? t("update_instance") : t("add_instance")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={isEdit ? t("save_changes") : t("add")}
    >
      <Form layout="vertical" form={form}>
        {/* Instance information */}
        <div className="row">
          <div className="col-12">
            <h5 className="mb-2">{t("instance_information")}</h5>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="name"
              label={t("name")}
              rules={[{ required: true, message: t("please_enter_name") }]}
            >
              <Input placeholder={t("name")} />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="establishmentName"
              label={t("certificate.form.establishmentName.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.establishmentName.required"),
                },
              ]}
            >
              <Input
                placeholder={t("certificate.form.establishmentName.label")}
              />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="phoneNumber"
              label={t("certificate.form.phoneNumber.label")}
              rules={[
                {
                  required: false,
                  message: t("validation.phoneNumber_required"),
                },
                {
                  validator: (_, value) =>
                    value && !isValidPhoneNumber(value)
                      ? Promise.reject(t("validation.phone_number_invalid"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.phoneNumber.label")} />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="address"
              label={t("certificate.form.address.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.address.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.address.label")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              name="zipCode"
              label={t("certificate.form.zipCode.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.zipCode.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.zipCode.label")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              name="academy"
              label={t("certificate.form.academy.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.academy.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.academy.label")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              name="prefecture"
              label={t("certificate.form.prefecture.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.prefecture.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.prefecture.label")} />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="commune"
              label={t("certificate.form.commune.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.commune.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.commune.label")} />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="directorName"
              label={t("certificate.form.directorName.label")}
              rules={[
                {
                  required: false,
                  message: t("certificate.form.directorName.required"),
                },
              ]}
            >
              <Input placeholder={t("certificate.form.directorName.label")} />
            </Form.Item>
          </div>
        </div>

        {/* ✅ Instance modules */}
        <div className="row mt-2">
          <div className="col-12">
            <h5 className="mb-2">{t("modules")}</h5>
          </div>

          <div className="col-md-6">
            <Form.Item
              name={["modelConfig", "hrm"]}
              label={t("hrm")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name={["modelConfig", "finance"]}
              label={t("finances")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        </div>

        {/* Director (account) information — CREATE ONLY */}
        {!isEdit && (
          <div className="row">
            <div className="col-12">
              <h5 className="mb-2">{t("director_information")}</h5>
            </div>

            <div className="col-md-4">
              <Form.Item
                name="directorFirstName"
                label={t("first_name")}
                rules={[
                  { required: true, message: t("validation.firstName_required") },
                ]}
              >
                <Input placeholder={t("first_name")} />
              </Form.Item>
            </div>

            <div className="col-md-4">
              <Form.Item
                name="directorLastName"
                label={t("last_name")}
                rules={[
                  { required: true, message: t("validation.lastName_required") },
                ]}
              >
                <Input placeholder={t("last_name")} />
              </Form.Item>
            </div>

            <div className="col-md-4">
              <Form.Item
                name="directorEmail"
                label={t("email")}
                rules={[
                  {
                    required: true,
                    message: t("validation.please_enter_email"),
                  },
                  {
                    type: "email",
                    message:
                      t("validation.please_enter_valid_email") || "Invalid email",
                  },
                ]}
              >
                <Input placeholder="name@example.com" />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item name="directorPhone" label={t("phone_number")}>
                <Input placeholder={t("phone_number")} />
              </Form.Item>
            </div>
          </div>
        )}
      </Form>
    </CustomModal>
  );
};

export default AddEditInstanceModal;
